import { useEffect, useState } from "react";
import axios from "axios";
import { API, api } from "@/lib/api";

/**
 * Organisation (builder) branding — the white-label layer, per tenant.
 *
 * One fetch is shared by every component (app shell, client portal, public quote / booking
 * pages). Which organisation? In order:
 *   1. an explicit org id (`setBrandingOrg`) — the API stamps every response it answered on
 *      behalf of a tenant with an `x-builtos-org` header, and a global axios interceptor
 *      forwards it here, so a public quote / notice page brands itself as the builder who
 *      sent it without any per-page wiring;
 *   2. `?org=` in the page URL;
 *   3. the signed-in user's organisation (GET /api/org with the session token);
 *   4. the platform's home organisation (GET /api/public/branding).
 * Company Settings calls `setBranding` after a save so logos and names update everywhere.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const store = { branding: null, promise: null, orgId: "" };
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn(store.branding));

export const POWERED_BY = { name: "BuiltOS", url: "/welcome", tagline: "Built by builders, for builders" };

/** Absolute URL for a branding asset path returned by the API ("/api/public/branding/logo.png?v=3&org=…"). */
export const brandAssetUrl = (path) => (path ? `${BACKEND_URL}${path}` : "");

const orgFromUrl = () => {
  try {
    return new URLSearchParams(window.location.search).get("org") || "";
  } catch {
    return "";
  }
};

const publicShape = (org) =>
  org
    ? {
        id: org.id,
        slug: org.slug,
        name: org.name,
        short_name: org.short_name,
        tagline: org.tagline,
        abn_label: org.abn_label,
        reg_label: org.reg_label,
        email: org.email,
        phone: org.phone,
        web: org.web,
        address: org.address,
        logo_version: org.logo_version,
        has_logo: org.has_logo,
        has_dark_logo: org.has_dark_logo,
        logo_url: org.logo_url,
        logo_dark_url: org.logo_dark_url,
        powered_by: org.powered_by || POWERED_BY,
        // invoice & payment details (bank account, terms, late-payment notice) — only present on
        // GET /api/org (signed-in); the public branding endpoint deliberately omits them
        invoice_settings: org.invoice_settings || null,
        // BuiltBuddy chat widget persona (avatar style + greeting) — Settings › Chat widget, admins only
        chat_widget: org.chat_widget || null,
      }
    : {};

export function loadBranding(force = false) {
  if (store.promise && !force) return store.promise;
  const orgId = store.orgId || orgFromUrl();
  const token = localStorage.getItem("bf_token");
  const publicUrl = `${API}/public/branding${orgId ? `?org=${encodeURIComponent(orgId)}` : ""}`;
  const fetchPublic = () => axios.get(publicUrl).then((res) => publicShape(res.data));
  // signed in → the user's own organisation (full shape incl. invoice & payment details) unless the
  // page is pinned to a *different* tenant (public quote / notice page), which keeps the public shape
  const request = token
    ? axios
        .get(`${API}/org`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => (!orgId || res.data?.id === orgId ? publicShape(res.data) : fetchPublic()))
        .catch(fetchPublic)
    : fetchPublic();
  store.promise = request
    .then((b) => {
      store.branding = b || {};
      if (store.branding.id) store.orgId = store.branding.id;
      emit();
      return store.branding;
    })
    .catch(() => {
      // never block the UI on branding — fall back to a neutral text lockup
      store.branding = store.branding || {};
      emit();
      return store.branding;
    });
  return store.promise;
}

/** Pin the branding to one organisation (public pages learn it from the API's x-builtos-org header). */
export function setBrandingOrg(orgId) {
  const next = orgId || "";
  if (!next || next === store.orgId) return;
  store.orgId = next;
  loadBranding(true);
}

/** Push a fresh org document (e.g. the PUT /api/org response) to every subscriber. */
export function setBranding(next) {
  store.branding = publicShape(next) || {};
  if (store.branding.id) store.orgId = store.branding.id;
  emit();
}

/** Forget the pinned organisation (sign-out / org switch) so the next load resolves afresh. */
export function resetBranding() {
  store.branding = null;
  store.promise = null;
  store.orgId = "";
}

// Every API answer given on behalf of a tenant carries x-builtos-org → keep the shell in sync.
const noteOrgHeader = (res) => {
  const org = res?.headers?.["x-builtos-org"];
  if (org) setBrandingOrg(org);
  return res;
};
const noteOrgError = (err) => {
  if (err?.response) noteOrgHeader(err.response);
  return Promise.reject(err);
};
axios.interceptors.response.use(noteOrgHeader, noteOrgError);
api.interceptors.response.use(noteOrgHeader, noteOrgError);

export function useBranding() {
  const [branding, setLocal] = useState(store.branding);

  useEffect(() => {
    listeners.add(setLocal);
    if (!store.branding) loadBranding();
    else setLocal(store.branding);
    return () => listeners.delete(setLocal);
  }, []);

  const b = branding || {};
  return {
    branding: b,
    loaded: branding !== null,
    orgId: b.id || store.orgId || "",
    name: b.name || "",
    shortName: b.short_name || b.name || "",
    tagline: b.tagline || "",
    hasLogo: Boolean(b.has_logo),
    hasDarkLogo: Boolean(b.has_dark_logo),
    logoUrl: brandAssetUrl(b.logo_url),
    logoDarkUrl: brandAssetUrl(b.logo_dark_url),
    poweredBy: b.powered_by || POWERED_BY,
    invoiceSettings: b.invoice_settings || null,
    chatWidget: b.chat_widget || null,
    refresh: () => loadBranding(true),
  };
}
